import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useConditionWalletsDashboard, QUERY_KEYS } from "@/hooks/use-queries";
import { Link } from "wouter";
import { 
  Card, CardContent, CardHeader, CardTitle, CardDescription 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select, SelectContent, SelectItem, 
  SelectTrigger, SelectValue 
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Heart, DollarSign, Users, TrendingUp, Plus,
  Check, X, Clock, AlertTriangle, Search,
  Download, RefreshCw, Eye, Edit, 
  ChevronRight, ArrowLeft, FileText, 
  CheckCircle2, XCircle, Wallet, Activity,
  PiggyBank, UserCheck, UserX, CreditCard
} from "lucide-react";

interface DashboardKPIs {
  totalConditions: number;
  activeConditions: number;
  totalWallets: number;
  activeWallets: number;
  totalMembers: number;
  pendingMembers: number;
  activeMembers: number;
  totalContributors: number;
  totalRaised: number;
  totalGoal: number;
  avgFundingProgress: number;
  pendingDisbursements: number;
  pendingRefunds: number;
  totalDisbursed: number;
  recentActivity: {
    newMembers24h: number;
    contributions24h: number;
    amountRaised24h: number;
  };
}

interface ConditionWallet {
  id: string;
  walletId: string;
  conditionId: string;
  title: string;
  summary: string;
  fundingGoal: number;
  totalRaised: number;
  memberCount: number;
  contributorCount: number;
  isFeatured: boolean;
  visibilityLevel: string;
  status: string;
  createdAt: string;
}

interface WalletMember {
  id: string;
  walletId: string;
  userId: string;
  status: string;
  verificationStatus: string;
  consentVersion: string;
  consentSignedAt: string;
  displayNamePublic: boolean;
  shareStoryPublic: boolean;
  createdAt: string;
}

interface PendingDisbursement {
  id: string;
  walletId: string;
  amount: number;
  recipientType: string;
  recipientId: string;
  status: string;
  requestedAt: string;
  description: string;
}

interface PendingRefund {
  id: string;
  walletId: string;
  contributorId: string;
  originalAmount: number;
  refundAmount: number;
  reason: string;
  status: string;
  requestedAt: string;
}

interface MedicalCondition {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  iconName: string;
  isFeatured: boolean;
  isActive: boolean;
  walletCount: number;
  totalRaised: number;
  totalMembers: number;
  nameEs?: string | null;
  nameSo?: string | null;
  nameHmn?: string | null;
  descriptionEs?: string | null;
  descriptionSo?: string | null;
  descriptionHmn?: string | null;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function KPICard({ 
  title, value, subValue, icon: Icon, trend, color = "primary" 
}: { 
  title: string; 
  value: string | number; 
  subValue?: string;
  icon: any; 
  trend?: { value: number; label: string };
  color?: "primary" | "success" | "warning" | "destructive";
}) {
  const colorClasses = {
    primary: "text-primary",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    destructive: "text-red-600 dark:text-red-400",
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${colorClasses[color]}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subValue && (
          <p className="text-xs text-muted-foreground mt-1">{subValue}</p>
        )}
        {trend && (
          <div className="flex items-center gap-1 mt-2">
            <TrendingUp className="h-3 w-3 text-green-600" />
            <span className="text-xs text-green-600">+{trend.value}</span>
            <span className="text-xs text-muted-foreground">{trend.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface DashboardResponse {
  kpis: DashboardKPIs;
  conditions: Array<{ id: string; name: string; walletCount: number; totalRaised: number }>;
  topWallets: Array<{ id: string; title: string; totalRaised: number; fundingGoal: number; memberCount: number; percentFunded: number }>;
}

function DashboardOverview() {
  const { data, isLoading } = useConditionWalletsDashboard();
  
  const kpis = data?.kpis;

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!kpis) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Unable to load dashboard data
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Conditions"
          value={kpis.totalConditions}
          subValue={`${kpis.activeConditions} active`}
          icon={Heart}
          color="primary"
        />
        <KPICard
          title="Active Wallets"
          value={kpis.activeWallets}
          subValue={`of ${kpis.totalWallets} total`}
          icon={Wallet}
          color="primary"
        />
        <KPICard
          title="Total Raised"
          value={formatCurrency(kpis.totalRaised || 0)}
          subValue={`${(kpis.avgFundingProgress || 0).toFixed(1)}% avg funding`}
          icon={DollarSign}
          color="success"
        />
        <KPICard
          title="Active Members"
          value={kpis.activeMembers}
          subValue={`${kpis.pendingMembers} pending approval`}
          icon={Users}
          trend={kpis.recentActivity ? { 
            value: kpis.recentActivity.newMembers24h, 
            label: "last 24h" 
          } : undefined}
          color="primary"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Contributors"
          value={kpis.totalContributors}
          subValue={kpis.recentActivity ? 
            `${kpis.recentActivity.contributions24h} contributions today` : undefined}
          icon={CreditCard}
          color="primary"
        />
        <KPICard
          title="Funding Progress"
          value={`${(kpis.avgFundingProgress || 0).toFixed(1)}%`}
          subValue={`Goal: ${formatCurrency(kpis.totalGoal || 0)}`}
          icon={PiggyBank}
          color="success"
        />
        <KPICard
          title="Pending Disbursements"
          value={kpis.pendingDisbursements}
          subValue="Awaiting approval"
          icon={Clock}
          color={kpis.pendingDisbursements > 0 ? "warning" : "primary"}
        />
        <KPICard
          title="Pending Refunds"
          value={kpis.pendingRefunds}
          subValue="Awaiting processing"
          icon={RefreshCw}
          color={kpis.pendingRefunds > 0 ? "warning" : "primary"}
        />
      </div>

      {kpis.recentActivity && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Last 24 Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <UserCheck className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">New Members</p>
                  <p className="text-2xl font-bold">{kpis.recentActivity.newMembers24h}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/20">
                  <Activity className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Contributions</p>
                  <p className="text-2xl font-bold">{kpis.recentActivity.contributions24h}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/20">
                  <DollarSign className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Amount Raised</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(kpis.recentActivity.amountRaised24h)}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function WalletsTable() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newWallet, setNewWallet] = useState({
    conditionId: "",
    title: "",
    summary: "",
    fundingGoal: "",
    visibilityLevel: "public",
    isFeatured: false,
  });

  const { data: wallets, isLoading } = useQuery<ConditionWallet[]>({
    queryKey: ["/api/admin/condition-wallets/analytics"],
  });

  const { data: conditions } = useQuery<MedicalCondition[]>({
    queryKey: ["/api/admin/conditions"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newWallet) => {
      return apiRequest("POST", "/api/admin/condition-wallets", {
        ...data,
        fundingGoal: Math.round(parseFloat(data.fundingGoal) * 100), // Convert to cents
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/condition-wallets/analytics"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conditionWalletsDashboard });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/conditions"] });
      toast({ title: "Wallet created successfully" });
      setCreateDialogOpen(false);
      setNewWallet({ conditionId: "", title: "", summary: "", fundingGoal: "", visibilityLevel: "public", isFeatured: false });
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to create wallet", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const handleCreateWallet = () => {
    if (!newWallet.conditionId || !newWallet.title || !newWallet.summary || !newWallet.fundingGoal) {
      toast({ 
        title: "Missing required fields", 
        description: "Please fill in all required fields",
        variant: "destructive" 
      });
      return;
    }
    createMutation.mutate(newWallet);
  };

  const filteredWallets = wallets?.filter(w => {
    const matchesSearch = w.title.toLowerCase().includes(search.toLowerCase()) ||
      w.walletId.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || w.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Condition Wallets</CardTitle>
            <CardDescription>Manage all condition-based wallets</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search wallets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-64"
                data-testid="input-search-wallets"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="select-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-create-wallet">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Wallet
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Condition Wallet</DialogTitle>
                  <DialogDescription>
                    Create a community health fund for a medical condition
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="wallet-condition">Condition *</Label>
                    <Select 
                      value={newWallet.conditionId} 
                      onValueChange={(value) => setNewWallet({ ...newWallet, conditionId: value })}
                    >
                      <SelectTrigger data-testid="select-wallet-condition">
                        <SelectValue placeholder="Select a condition" />
                      </SelectTrigger>
                      <SelectContent>
                        {conditions?.map((condition) => (
                          <SelectItem key={condition.id} value={condition.id}>
                            {condition.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="wallet-title">Title *</Label>
                    <Input
                      id="wallet-title"
                      placeholder="e.g., Twin Cities Diabetes Support Fund"
                      value={newWallet.title}
                      onChange={(e) => setNewWallet({ ...newWallet, title: e.target.value })}
                      data-testid="input-wallet-title"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="wallet-summary">Summary *</Label>
                    <Textarea
                      id="wallet-summary"
                      placeholder="Describe the purpose of this fund..."
                      value={newWallet.summary}
                      onChange={(e) => setNewWallet({ ...newWallet, summary: e.target.value })}
                      rows={3}
                      data-testid="input-wallet-summary"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="wallet-goal">Funding Goal ($) *</Label>
                    <Input
                      id="wallet-goal"
                      type="number"
                      placeholder="e.g., 50000"
                      value={newWallet.fundingGoal}
                      onChange={(e) => setNewWallet({ ...newWallet, fundingGoal: e.target.value })}
                      data-testid="input-wallet-goal"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="wallet-visibility">Visibility</Label>
                    <Select 
                      value={newWallet.visibilityLevel} 
                      onValueChange={(value) => setNewWallet({ ...newWallet, visibilityLevel: value })}
                    >
                      <SelectTrigger data-testid="select-wallet-visibility">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="members">Members Only</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateWallet} 
                    disabled={createMutation.isPending}
                    data-testid="button-save-wallet"
                  >
                    {createMutation.isPending ? "Creating..." : "Create Wallet"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Wallet</TableHead>
              <TableHead>Funding</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Contributors</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredWallets?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No wallets found
                </TableCell>
              </TableRow>
            ) : (
              filteredWallets?.map((wallet) => {
                const progress = wallet.fundingGoal > 0 
                  ? Math.min(100, (wallet.totalRaised / wallet.fundingGoal) * 100) 
                  : 0;
                return (
                  <TableRow key={wallet.id} data-testid={`row-wallet-${wallet.id}`}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{wallet.title}</span>
                        <span className="text-xs text-muted-foreground">{wallet.walletId}</span>
                        {wallet.isFeatured && (
                          <Badge variant="secondary" className="w-fit mt-1">Featured</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{formatCurrency(wallet.totalRaised)}</span>
                          <span className="text-muted-foreground text-sm">
                            / {formatCurrency(wallet.fundingGoal)}
                          </span>
                        </div>
                        <Progress value={progress} className="h-2 w-24" />
                        <span className="text-xs text-muted-foreground">{progress.toFixed(1)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{wallet.memberCount}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{wallet.contributorCount}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        wallet.status === "active" ? "default" : 
                        wallet.status === "completed" ? "secondary" : "outline"
                      }>
                        {wallet.status || "active"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" asChild>
                          <Link href={`/conditions/${wallet.conditionId}/${wallet.walletId}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" data-testid={`button-edit-wallet-${wallet.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

type EditConditionData = {
  name: string;
  description: string;
  nameEs: string;
  descriptionEs: string;
  nameSo: string;
  descriptionSo: string;
  nameHmn: string;
  descriptionHmn: string;
  isFeatured: boolean;
  isActive: boolean;
  category: string;
};

function ConditionsTable() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCondition, setNewCondition] = useState({
    name: "",
    slug: "",
    description: "",
    category: "chronic",
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCondition, setEditingCondition] = useState<MedicalCondition | null>(null);
  const [editData, setEditData] = useState<EditConditionData>({
    name: "", description: "",
    nameEs: "", descriptionEs: "",
    nameSo: "", descriptionSo: "",
    nameHmn: "", descriptionHmn: "",
    isFeatured: false, isActive: true, category: "chronic",
  });

  const { data: conditions, isLoading } = useQuery<MedicalCondition[]>({
    queryKey: ["/api/admin/conditions"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newCondition) => {
      return apiRequest("POST", "/api/admin/conditions", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/conditions"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conditionWalletsDashboard });
      toast({ title: "Condition created successfully" });
      setCreateDialogOpen(false);
      setNewCondition({ name: "", slug: "", description: "", category: "chronic" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to create condition", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<EditConditionData> }) => {
      return apiRequest("PATCH", `/api/admin/conditions/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/conditions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/public/conditions"] });
      toast({ title: "Condition updated successfully" });
      setEditDialogOpen(false);
      setEditingCondition(null);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update condition",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleOpenEdit = (condition: MedicalCondition) => {
    setEditingCondition(condition);
    setEditData({
      name: condition.name ?? "",
      description: condition.description ?? "",
      nameEs: condition.nameEs ?? "",
      descriptionEs: condition.descriptionEs ?? "",
      nameSo: condition.nameSo ?? "",
      descriptionSo: condition.descriptionSo ?? "",
      nameHmn: condition.nameHmn ?? "",
      descriptionHmn: condition.descriptionHmn ?? "",
      isFeatured: condition.isFeatured ?? false,
      isActive: condition.isActive ?? true,
      category: condition.category ?? "chronic",
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingCondition) return;
    editMutation.mutate({ id: editingCondition.id, data: editData });
  };

  const filteredConditions = conditions?.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.slug.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === "all" || c.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleCreateCondition = () => {
    if (!newCondition.name || !newCondition.slug || !newCondition.description) {
      toast({ 
        title: "Missing required fields", 
        description: "Name, slug, and description are required",
        variant: "destructive" 
      });
      return;
    }
    createMutation.mutate(newCondition);
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Medical Conditions</CardTitle>
            <CardDescription>Manage condition categories for community health funds</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search conditions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-64"
                data-testid="input-search-conditions"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36" data-testid="select-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="chronic">Chronic</SelectItem>
                <SelectItem value="cancer">Cancer</SelectItem>
                <SelectItem value="rare">Rare Disease</SelectItem>
                <SelectItem value="transplant">Transplant</SelectItem>
                <SelectItem value="pediatric">Pediatric</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-add-condition">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Condition
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Medical Condition</DialogTitle>
                  <DialogDescription>
                    Create a new condition category for community health funds
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="condition-name">Name *</Label>
                    <Input
                      id="condition-name"
                      placeholder="e.g., Type 2 Diabetes"
                      value={newCondition.name}
                      onChange={(e) => {
                        setNewCondition({
                          ...newCondition,
                          name: e.target.value,
                          slug: generateSlug(e.target.value),
                        });
                      }}
                      data-testid="input-condition-name"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="condition-slug">Slug *</Label>
                    <Input
                      id="condition-slug"
                      placeholder="e.g., type-2-diabetes"
                      value={newCondition.slug}
                      onChange={(e) => setNewCondition({ ...newCondition, slug: e.target.value })}
                      data-testid="input-condition-slug"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="condition-category">Category</Label>
                    <Select 
                      value={newCondition.category} 
                      onValueChange={(value) => setNewCondition({ ...newCondition, category: value })}
                    >
                      <SelectTrigger data-testid="select-condition-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chronic">Chronic</SelectItem>
                        <SelectItem value="cancer">Cancer</SelectItem>
                        <SelectItem value="rare">Rare Disease</SelectItem>
                        <SelectItem value="transplant">Transplant</SelectItem>
                        <SelectItem value="pediatric">Pediatric</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="condition-description">Description *</Label>
                    <Textarea
                      id="condition-description"
                      placeholder="Describe this medical condition..."
                      value={newCondition.description}
                      onChange={(e) => setNewCondition({ ...newCondition, description: e.target.value })}
                      rows={3}
                      data-testid="input-condition-description"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateCondition} 
                    disabled={createMutation.isPending}
                    data-testid="button-save-condition"
                  >
                    {createMutation.isPending ? "Creating..." : "Create Condition"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Condition</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Wallets</TableHead>
              <TableHead>Total Raised</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredConditions?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No conditions found
                </TableCell>
              </TableRow>
            ) : (
              filteredConditions?.map((condition) => (
                <TableRow key={condition.id} data-testid={`row-condition-${condition.id}`}>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">{condition.name}</span>
                      <span className="text-xs text-muted-foreground">{condition.slug}</span>
                      <div className="flex items-center gap-1 flex-wrap">
                        {condition.isFeatured && (
                          <Badge variant="secondary" className="text-xs">Featured</Badge>
                        )}
                        <Badge variant={condition.nameEs ? "default" : "outline"} className="text-xs">ES</Badge>
                        <Badge variant={condition.nameSo ? "default" : "outline"} className="text-xs">SO</Badge>
                        <Badge variant={condition.nameHmn ? "default" : "outline"} className="text-xs">HMN</Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{condition.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{condition.walletCount}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{formatCurrency(condition.totalRaised)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{condition.totalMembers}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={condition.isActive ? "default" : "secondary"}>
                      {condition.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" asChild>
                        <Link href={`/conditions/${condition.slug}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`button-edit-condition-${condition.id}`}
                        onClick={() => handleOpenEdit(condition)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) { setEditDialogOpen(false); setEditingCondition(null); } }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Condition</DialogTitle>
          <DialogDescription>
            Update condition details and translations. Filled language badges appear in the list.
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="en" className="mt-2">
          <TabsList className="flex gap-1">
            <TabsTrigger value="en" data-testid="tab-lang-en">English</TabsTrigger>
            <TabsTrigger value="es" data-testid="tab-lang-es">
              Español
              {editData.nameEs ? <span className="ml-1 h-1.5 w-1.5 rounded-full bg-green-500 inline-block" /> : null}
            </TabsTrigger>
            <TabsTrigger value="so" data-testid="tab-lang-so">
              Somali
              {editData.nameSo ? <span className="ml-1 h-1.5 w-1.5 rounded-full bg-green-500 inline-block" /> : null}
            </TabsTrigger>
            <TabsTrigger value="hmn" data-testid="tab-lang-hmn">
              Hmong
              {editData.nameHmn ? <span className="ml-1 h-1.5 w-1.5 rounded-full bg-green-500 inline-block" /> : null}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="en" className="mt-4 space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name-en">Name (English)</Label>
              <Input
                id="edit-name-en"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                data-testid="input-edit-name-en"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-desc-en">Description (English)</Label>
              <Textarea
                id="edit-desc-en"
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                rows={4}
                data-testid="input-edit-desc-en"
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editData.isActive}
                  onChange={(e) => setEditData({ ...editData, isActive: e.target.checked })}
                  data-testid="checkbox-edit-active"
                />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editData.isFeatured}
                  onChange={(e) => setEditData({ ...editData, isFeatured: e.target.checked })}
                  data-testid="checkbox-edit-featured"
                />
                <span className="text-sm">Featured</span>
              </label>
            </div>
          </TabsContent>

          <TabsContent value="es" className="mt-4 space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name-es">Name (Español)</Label>
              <Input
                id="edit-name-es"
                value={editData.nameEs}
                onChange={(e) => setEditData({ ...editData, nameEs: e.target.value })}
                placeholder="Nombre en español"
                data-testid="input-edit-name-es"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-desc-es">Description (Español)</Label>
              <Textarea
                id="edit-desc-es"
                value={editData.descriptionEs}
                onChange={(e) => setEditData({ ...editData, descriptionEs: e.target.value })}
                placeholder="Descripción en español"
                rows={4}
                data-testid="input-edit-desc-es"
              />
            </div>
          </TabsContent>

          <TabsContent value="so" className="mt-4 space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name-so">Name (Somali)</Label>
              <Input
                id="edit-name-so"
                value={editData.nameSo}
                onChange={(e) => setEditData({ ...editData, nameSo: e.target.value })}
                placeholder="Magaca Soomaaliga"
                data-testid="input-edit-name-so"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-desc-so">Description (Somali)</Label>
              <Textarea
                id="edit-desc-so"
                value={editData.descriptionSo}
                onChange={(e) => setEditData({ ...editData, descriptionSo: e.target.value })}
                placeholder="Sharraxaadda Soomaaliga"
                rows={4}
                data-testid="input-edit-desc-so"
              />
            </div>
          </TabsContent>

          <TabsContent value="hmn" className="mt-4 space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name-hmn">Name (Hmong)</Label>
              <Input
                id="edit-name-hmn"
                value={editData.nameHmn}
                onChange={(e) => setEditData({ ...editData, nameHmn: e.target.value })}
                placeholder="Lub npe Hmoob"
                data-testid="input-edit-name-hmn"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-desc-hmn">Description (Hmong)</Label>
              <Textarea
                id="edit-desc-hmn"
                value={editData.descriptionHmn}
                onChange={(e) => setEditData({ ...editData, descriptionHmn: e.target.value })}
                placeholder="Piav qhia Hmoob"
                rows={4}
                data-testid="input-edit-desc-hmn"
              />
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingCondition(null); }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            disabled={editMutation.isPending}
            data-testid="button-save-condition"
          >
            {editMutation.isPending ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function PendingMembersTable() {
  const { toast } = useToast();
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const { data: members, isLoading } = useQuery<WalletMember[]>({
    queryKey: ["/api/admin/condition-wallets/members", { status: "pending" }],
  });

  const approveMutation = useMutation({
    mutationFn: async (memberIds: string[]) => {
      return apiRequest("POST", "/api/admin/condition-wallets/members/bulk-approve", { memberIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/condition-wallets/members"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conditionWalletsDashboard });
      toast({ title: "Members approved successfully" });
      setSelectedMembers([]);
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to approve members", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  const toggleSelect = (id: string) => {
    setSelectedMembers(prev => 
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedMembers.length === members?.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members?.map(m => m.id) || []);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Pending Membership Applications</CardTitle>
            <CardDescription>
              {members?.length || 0} applications awaiting approval
            </CardDescription>
          </div>
          {selectedMembers.length > 0 && (
            <div className="flex gap-2">
              <Button
                onClick={() => approveMutation.mutate(selectedMembers)}
                disabled={approveMutation.isPending}
                data-testid="button-bulk-approve"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Approve Selected ({selectedMembers.length})
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {members?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <UserCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No pending applications</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={selectedMembers.length === members?.length && members?.length > 0}
                    onChange={toggleAll}
                    className="rounded border-gray-300"
                    data-testid="checkbox-select-all"
                  />
                </TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Wallet</TableHead>
                <TableHead>Consent</TableHead>
                <TableHead>Applied</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members?.map((member) => (
                <TableRow key={member.id} data-testid={`row-member-${member.id}`}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(member.id)}
                      onChange={() => toggleSelect(member.id)}
                      className="rounded border-gray-300"
                    />
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">{member.userId.slice(0, 8)}...</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">{member.walletId.slice(0, 8)}...</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {member.displayNamePublic && (
                        <Badge variant="outline" className="text-xs">Name</Badge>
                      )}
                      {member.shareStoryPublic && (
                        <Badge variant="outline" className="text-xs">Story</Badge>
                      )}
                      <span className="text-xs text-muted-foreground">v{member.consentVersion}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDate(member.createdAt)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => approveMutation.mutate([member.id])}
                        disabled={approveMutation.isPending}
                        data-testid={`button-approve-${member.id}`}
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        data-testid={`button-reject-${member.id}`}
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function PendingDisbursementsTable() {
  const { toast } = useToast();

  const { data: disbursements, isLoading } = useQuery<PendingDisbursement[]>({
    queryKey: ["/api/admin/condition-wallets/disbursements", { status: "pending" }],
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/admin/condition-wallets/disbursements/${id}/approve`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/condition-wallets/disbursements"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conditionWalletsDashboard });
      toast({ title: "Disbursement approved" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to approve disbursement", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Disbursements</CardTitle>
        <CardDescription>
          {disbursements?.length || 0} disbursements awaiting approval
        </CardDescription>
      </CardHeader>
      <CardContent>
        {disbursements?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No pending disbursements</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Wallet</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {disbursements?.map((d) => (
                <TableRow key={d.id} data-testid={`row-disbursement-${d.id}`}>
                  <TableCell>
                    <span className="font-mono text-sm">{d.walletId.slice(0, 8)}...</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm capitalize">{d.recipientType}</span>
                      <span className="text-xs text-muted-foreground font-mono">
                        {d.recipientId.slice(0, 8)}...
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{formatCurrency(d.amount)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{d.description}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDate(d.requestedAt)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => approveMutation.mutate(d.id)}
                        disabled={approveMutation.isPending}
                        data-testid={`button-approve-disbursement-${d.id}`}
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        data-testid={`button-reject-disbursement-${d.id}`}
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function PendingRefundsTable() {
  const { toast } = useToast();

  const { data: refunds, isLoading } = useQuery<PendingRefund[]>({
    queryKey: ["/api/admin/condition-wallets/refunds", { status: "pending" }],
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/admin/condition-wallets/refunds/${id}/approve`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/condition-wallets/refunds"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conditionWalletsDashboard });
      toast({ title: "Refund approved" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to approve refund", 
        description: error.message,
        variant: "destructive" 
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Refunds</CardTitle>
        <CardDescription>
          {refunds?.length || 0} refunds awaiting processing
        </CardDescription>
      </CardHeader>
      <CardContent>
        {refunds?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <RefreshCw className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No pending refunds</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Wallet</TableHead>
                <TableHead>Original Amount</TableHead>
                <TableHead>Refund Amount</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {refunds?.map((r) => (
                <TableRow key={r.id} data-testid={`row-refund-${r.id}`}>
                  <TableCell>
                    <span className="font-mono text-sm">{r.walletId.slice(0, 8)}...</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{formatCurrency(r.originalAmount)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{formatCurrency(r.refundAmount)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{r.reason}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDate(r.requestedAt)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => approveMutation.mutate(r.id)}
                        disabled={approveMutation.isPending}
                        data-testid={`button-approve-refund-${r.id}`}
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        data-testid={`button-reject-refund-${r.id}`}
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

interface WalletContributor {
  id: string;
  walletId: string;
  userId: string | null;
  displayName: string | null;
  organizationName: string | null;
  contactEmail: string | null;
  totalContributed: number;
  contributionCount: number;
  isAnonymous: boolean;
  showOnLeaderboard: boolean;
  createdAt: string;
}

interface LedgerEntry {
  id: string;
  walletId: string;
  entryType: string;
  amount: number;
  debitAccountType: string;
  creditAccountType: string;
  description: string;
  createdAt: string;
}

function ContributorsTable() {
  const [search, setSearch] = useState("");

  const { data: contributors, isLoading } = useQuery<WalletContributor[]>({
    queryKey: ["/api/admin/condition-wallets/contributors"],
  });

  const filteredContributors = contributors?.filter(c => 
    (c.displayName?.toLowerCase().includes(search.toLowerCase())) ||
    (c.organizationName?.toLowerCase().includes(search.toLowerCase())) ||
    (c.contactEmail?.toLowerCase().includes(search.toLowerCase()))
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Contributors</CardTitle>
            <CardDescription>All contributors across condition wallets</CardDescription>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search contributors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
              data-testid="input-search-contributors"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredContributors?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No contributors found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contributor</TableHead>
                <TableHead>Total Contributed</TableHead>
                <TableHead>Contributions</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead>First Contribution</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContributors?.map((c) => (
                <TableRow key={c.id} data-testid={`row-contributor-${c.id}`}>
                  <TableCell>
                    <div className="flex flex-col">
                      {c.isAnonymous ? (
                        <span className="text-muted-foreground italic">Anonymous</span>
                      ) : (
                        <>
                          <span className="font-medium">
                            {c.displayName || c.organizationName || "Unknown"}
                          </span>
                          {c.contactEmail && (
                            <span className="text-xs text-muted-foreground">{c.contactEmail}</span>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-green-600">
                      {formatCurrency(c.totalContributed)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{c.contributionCount}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {c.isAnonymous && (
                        <Badge variant="outline" className="text-xs">Anonymous</Badge>
                      )}
                      {c.showOnLeaderboard && (
                        <Badge variant="outline" className="text-xs">Leaderboard</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDate(c.createdAt)}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function LedgerTable() {
  const [walletId, setWalletId] = useState("");

  const { data: entries, isLoading } = useQuery<LedgerEntry[]>({
    queryKey: ["/api/admin/condition-wallets/ledger", { walletId: walletId || undefined }],
    enabled: !!walletId,
  });

  const { data: wallets } = useQuery<ConditionWallet[]>({
    queryKey: ["/api/admin/condition-wallets/analytics"],
  });

  if (isLoading && walletId) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Financial Ledger</CardTitle>
            <CardDescription>Double-entry accounting records</CardDescription>
          </div>
          <Select value={walletId} onValueChange={setWalletId}>
            <SelectTrigger className="w-72" data-testid="select-wallet-ledger">
              <SelectValue placeholder="Select a wallet" />
            </SelectTrigger>
            <SelectContent>
              {wallets?.map((w) => (
                <SelectItem key={w.walletId} value={w.walletId}>
                  {w.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {!walletId ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Select a wallet to view ledger entries</p>
          </div>
        ) : entries?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No ledger entries for this wallet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Debit</TableHead>
                <TableHead>Credit</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries?.map((entry) => (
                <TableRow key={entry.id} data-testid={`row-ledger-${entry.id}`}>
                  <TableCell>
                    <span className="text-sm">{formatDate(entry.createdAt)}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      entry.entryType === "contribution" ? "default" :
                      entry.entryType === "disbursement" ? "secondary" :
                      entry.entryType === "refund" ? "destructive" : "outline"
                    }>
                      {entry.entryType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={`font-medium ${
                      entry.entryType === "contribution" ? "text-green-600" : 
                      entry.entryType === "refund" ? "text-red-600" : ""
                    }`}>
                      {formatCurrency(entry.amount)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground capitalize">
                      {entry.debitAccountType.replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground capitalize">
                      {entry.creditAccountType.replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{entry.description}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function ExportSection() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState<string | null>(null);

  const handleExport = async (type: string) => {
    setExporting(type);
    try {
      const response = await fetch(`/api/admin/condition-wallets/export?type=${type}`);
      if (!response.ok) throw new Error("Export failed");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}-export-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast({ title: `${type} data exported successfully` });
    } catch (error: any) {
      toast({ 
        title: "Export failed", 
        description: error.message,
        variant: "destructive" 
      });
    } finally {
      setExporting(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Data Export</CardTitle>
        <CardDescription>Export data for reporting and compliance</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("wallets")}
            disabled={exporting !== null}
            data-testid="button-export-wallets"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting === "wallets" ? "Exporting..." : "Export Wallets"}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("members")}
            disabled={exporting !== null}
            data-testid="button-export-members"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting === "members" ? "Exporting..." : "Export Members"}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("contributions")}
            disabled={exporting !== null}
            data-testid="button-export-contributions"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting === "contributions" ? "Exporting..." : "Export Contributions"}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("audit")}
            disabled={exporting !== null}
            data-testid="button-export-audit"
          >
            <FileText className="h-4 w-4 mr-2" />
            {exporting === "audit" ? "Exporting..." : "Export Audit Log"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminConditionWallets() {
  usePageTitle("Condition Wallet Management", "Manage condition-based community health wallets and review contributions across the HealthWallet platform.");
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div className="container max-w-screen-2xl py-6 space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" asChild>
          <Link href="/admin-control">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Condition Wallet Administration</h1>
          <p className="text-muted-foreground">
            Manage community health funds, memberships, and financial operations
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:grid-cols-none lg:flex flex-wrap gap-1">
          <TabsTrigger value="overview" data-testid="tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="conditions" data-testid="tab-conditions">
            Conditions
          </TabsTrigger>
          <TabsTrigger value="wallets" data-testid="tab-wallets">
            Wallets
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">
            Members
          </TabsTrigger>
          <TabsTrigger value="contributors" data-testid="tab-contributors">
            Contributors
          </TabsTrigger>
          <TabsTrigger value="disbursements" data-testid="tab-disbursements">
            Disbursements
          </TabsTrigger>
          <TabsTrigger value="refunds" data-testid="tab-refunds">
            Refunds
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">
            Ledger
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <DashboardOverview />
          <ExportSection />
        </TabsContent>

        <TabsContent value="conditions">
          <ConditionsTable />
        </TabsContent>

        <TabsContent value="wallets">
          <WalletsTable />
        </TabsContent>

        <TabsContent value="members">
          <PendingMembersTable />
        </TabsContent>

        <TabsContent value="contributors">
          <ContributorsTable />
        </TabsContent>

        <TabsContent value="disbursements">
          <PendingDisbursementsTable />
        </TabsContent>

        <TabsContent value="refunds">
          <PendingRefundsTable />
        </TabsContent>

        <TabsContent value="ledger">
          <LedgerTable />
        </TabsContent>
      </Tabs>
    </div>
  );
}
